// src/lib/contractSectionFields.js — WHICH QUESTIONS A SWITCHED-OFF SECTION TAKES WITH IT.
//
// Owner, 2026-09-12: "when I hide sections let it also hide in filling fields too." Switching a
// section off removed its text from the document and left its questions standing in the form,
// so the agent kept filling in a figure that no longer appeared anywhere — and Save kept
// waiting for it.
//
// 🔑 THE LINK IS DERIVED, NOT DECLARED. A clause asks for a field by writing `{{key}}` in its
// own text, and that is the same string the renderer substitutes (lib/contract_render.js). So a
// field belongs to the sections whose text names it, and nothing has to be maintained per
// template. That matters twice: the agent's OWN edit to a clause counts — delete the marker and
// the question goes with it — and a template added later needs no list here. The owner is about
// to add a fourth contract type; a table would have been wrong the day it arrived.
//
// ⚠️ ONLY-IN-DISABLED, never "absent from the enabled ones". A field named by no clause at all —
// one that feeds a derived line such as `{{prepay_summary}}`, or the parties block, or a value
// the server computes — must keep being asked. Hiding by absence would quietly empty the form of
// exactly the questions whose wiring is least obvious.
//
// ⚠️ Required and conditional sections are never off (contract_sections.js keeps them on), so
// their markers always count as asked, even if a stale client state says enabled: false.
//
// Pure on purpose: no React, no i18n, so the rule can be tested directly
// (src/lib/contractSectionFields.test.mjs) rather than only through a rendered page.

// `{{key.cal_days}}` (2026-09-21) prints the same field another way — it names `key`
const MARKER = /\{\{([a-z0-9_]+)(?:\.[a-z_]+)?\}\}/g

// Every `{{placeholder}}` in one section's clause text, in every language it carries. All three
// are read because a marker may survive in one column and be edited out of another, and a
// question is asked as long as ANY column still asks it.
export function markersIn(section) {
  const out = new Set()
  for (const c of section?.clauses || []) {
    for (const lang of ['text_ka', 'text_en', 'text_ru']) {
      const txt = c && c[lang]
      if (!txt) continue
      MARKER.lastIndex = 0
      let m
      while ((m = MARKER.exec(txt))) out.add(m[1])
    }
  }
  return out
}

// The field keys the form should stop asking for: named by a switched-off section, and by no
// section that is still in the document.
export function fieldsHiddenBySections(sections) {
  const hidden = new Set()
  if (!Array.isArray(sections) || !sections.length) return hidden
  const asked = new Set()
  for (const s of sections) {
    const off = s && s.enabled === false && !s.mandatory && s.removable !== false
    for (const k of markersIn(s)) (off ? hidden : asked).add(k)
  }
  for (const k of asked) hidden.delete(k)
  return hidden
}
